/* eslint-disable react/jsx-no-useless-fragment */
import {
  Box,
  InputElement,
  Menu,
  NumberInputRootProps,
  Portal,
  useDisclosure,
} from '@chakra-ui/react';
import { AmountUnitTypeEnum } from '@monetize/types/app';
import { getCurrencySymbol } from '@monetize/utils/core';
import React, { FunctionComponent as FC, FocusEvent, useState } from 'react';
import { MdArrowDropDown } from 'react-icons/md';
import { AMOUNT_UNITS } from '../../constants';
import { useToast } from '../../services/toast';
import { MBox } from './chakra';
import MIconButton from './MIconButton';
import { MTooltip } from './MTooltip';
import DiscountTargetPriceModal, {
  DiscountTargetPriceModalAmountOptions,
  SET_TARGET_VALUE_DISCOUNT_TYPE_OPTION,
} from './DiscountTargetPriceModal';
import { MCustomNumberInput } from './MCustomNumberInput';

export interface MAmountWithUnitTypeInputProps extends Omit<
  NumberInputRootProps,
  'onChange'
> {
  amountUnitType?: AmountUnitTypeEnum | null;
  amountCeiling?: number;
  // v2 shipped `isDisabled`/`isReadOnly` on `NumberInput`; the real v3
  // `NumberInputProps` only has `disabled`/`readOnly` — forwarded to
  // `MCustomNumberInput` below, which does its own translation.
  isDisabled?: boolean;
  isReadOnly?: boolean;
  // Same as `MCustomNumberInputProps.onChange` — this is a passthrough to
  // it, so it carries the identical v2-style `(valueAsString,
  // valueAsNumber)` signature, not the inherited native `<div onChange>`.
  onChange?: (valueAsString: string, valueAsNumber: number) => void;
  containerColors?: {
    color: string;
    bgColor: string;
    borderColor: string;
  };
  currency?: string;
  onChangeAmountUnitType?: (amountUnitType: AmountUnitTypeEnum) => void;
  // https://www.figma.com/proto/37LoEpFHHM90nSL6uNgrmh/Discount-on-Quotes?page-id=13%3A52&node-id=219%3A22382&viewport=722%2C604%2C0.15&scaling=scale-down&starting-point-node-id=219%3A23861
  targetAmountOptions?: DiscountTargetPriceModalAmountOptions;
  disableUnitTypeSelect?: boolean;
  disableUnitTypeMessage?: string;
  usePortal?: boolean;
  maxWidth?: number | string;
  placeholder?: string;
  additionalMenuItems?: React.ReactNode[];
}

const MAmountWithUnitTypeInput: FC<MAmountWithUnitTypeInputProps> =
  React.forwardRef<any, MAmountWithUnitTypeInputProps>(
    (
      {
        amountUnitType = AmountUnitTypeEnum.FLAT,
        amountCeiling = Infinity,
        currency,
        onChangeAmountUnitType = () => null,
        isDisabled,
        containerColors = {
          color: 'tIndigo.base',
          bgColor: 'tWhite.base',
          borderColor: 'tBlue.lightShade',
        },
        onFocus,
        onBlur,
        value,
        targetAmountOptions,
        usePortal = false,
        onChange,
        disableUnitTypeSelect = false,
        disableUnitTypeMessage = '',
        isReadOnly,
        maxWidth = '7rem',
        additionalMenuItems = [],
        ...rest
      }: MAmountWithUnitTypeInputProps,
      ref: any,
    ) => {
      const { addToast } = useToast();

      const {
        open: isTargetAmountModalOpen,
        onOpen: onOpenTargetAmountModal,
        onClose: onCloseTargetAmountModal,
      } = useDisclosure();

      // To support amendment and renewals colors for quote
      const containerColorsWithDisabledState = isReadOnly
        ? containerColors
        : {
            ...containerColors,
            bgColor: isDisabled ? 'tGray.back' : containerColors?.bgColor,
          };
      const [isFocused, setIsFocused] = useState(false);

      const handleInputFocus = (ev: FocusEvent<HTMLInputElement>) => {
        setIsFocused(true);
        onFocus && onFocus(ev);
      };
      const handleInputBlur = (ev: FocusEvent<HTMLInputElement>) => {
        setIsFocused(false);
        onBlur && onBlur(ev);
      };

      const onInternalChangeAmountUnitType = (unitType: string) => {
        if (unitType === SET_TARGET_VALUE_DISCOUNT_TYPE_OPTION.value) {
          if (!targetAmountOptions) {
            return;
          }

          if (targetAmountOptions?.amountWithoutDiscount <= 0) {
            addToast({
              summary: 'Total Amount',
              detail: `Total amount should be greater than 0.`,
              severity: 'warning',
            });
          } else {
            onOpenTargetAmountModal();
          }
        } else {
          onChangeAmountUnitType &&
            onChangeAmountUnitType(unitType as AmountUnitTypeEnum);
        }
      };

      const onChangeDiscountTargetPrice = (val: number) => {
        onChangeAmountUnitType &&
          onChangeAmountUnitType(AmountUnitTypeEnum.PERCENTAGE);
        onChange && onChange(String(val), val);
      };

      const isPercent = amountUnitType === AmountUnitTypeEnum.PERCENTAGE;
      const isFlat = amountUnitType === AmountUnitTypeEnum.FLAT;

      const FLAT_PERCENT_STYLE = {
        fontSize: 14,
        fontWeight: 700,
        minW: isFlat ? 2 : 4,
        mr: 1,
      };

      return (
        <Box
          position="relative"
          maxWidth={maxWidth}
          borderRadius="sm"
          {...containerColorsWithDisabledState}
          color={isFocused ? 'tIndigo.base' : 'tPurple.base'}
        >
          {/* Both elements restore what v2's Input*Element had: `top: 0`
              (v3 sets none, so outside a flex group the end element sits at
              its static position, below the input), no `px: 3`, so the
              fixed widths below are the full content width, and an
              inherited color instead of `fg.muted`, so the symbols follow
              the wrapper's focus color. */}
          {isFlat && (
            <InputElement
              placement="start"
              top="0"
              w={4}
              px="0"
              color="inherit"
              justifyContent="flex-end"
            >
              <Box {...FLAT_PERCENT_STYLE}>
                {currency && getCurrencySymbol(currency)}
              </Box>
            </InputElement>
          )}
          <MCustomNumberInput
            inputMode="decimal"
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            styleProps={{
              paddingRight: isPercent
                ? targetAmountOptions && !isDisabled && !isReadOnly
                  ? 12
                  : 6
                : 8,
              paddingLeft: isFlat ? 4 : 2,
              textOverflow: 'ellipsis',
            }}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            value={value}
            max={amountCeiling}
            onChange={onChange}
            {...rest}
          />

          <InputElement
            placement="end"
            top="0"
            w={
              isReadOnly
                ? 16
                : isPercent
                  ? targetAmountOptions && !isDisabled && !isReadOnly
                    ? 12
                    : 6
                  : 8
            }
            px="0"
            color="inherit"
            zIndex="unset"
            justifyContent={
              targetAmountOptions && !isDisabled && !isReadOnly
                ? 'center'
                : 'flex-end'
            }
          >
            {isPercent && (
              <Box {...FLAT_PERCENT_STYLE}>
                {
                  AMOUNT_UNITS.AMOUNT_UNIT_DISPLAY_SYMBOL[
                    AmountUnitTypeEnum.PERCENTAGE
                  ]
                }
              </Box>
            )}
            {targetAmountOptions && !isDisabled && !isReadOnly && (
              <MBox width="1px" bg="border.default" height="calc(100% - 2px)" />
            )}
            {targetAmountOptions && !isDisabled && !isReadOnly && (
              <Menu.Root
                lazyMount
                unmountOnExit
                positioning={{
                  offset: { crossAxis: 0, mainAxis: 2 },
                }}
              >
                {/* `unstyled` — the menu recipe's trigger styles (its 32px
                    height, text color, gray hover) would otherwise override
                    the button's own. v2's `MenuButton as={IconButton}` never
                    applied them. */}
                <Menu.Trigger asChild unstyled>
                  <MIconButton
                    aria-label="Options"
                    icon={<MdArrowDropDown />}
                    variant="outline"
                    // 28px: the input's height inside its 1px borders, like
                    // the divider
                    h={7}
                    maxW={6}
                    minW={6}
                    borderStyle="none"
                    // Square against the divider; on the right, follow the
                    // curve inside the input's border
                    borderLeftRadius="none"
                    borderRightRadius="xs"
                    _hover={{
                      bg: 'transparent',
                    }}
                    css={{
                      '&[data-active]': {
                        background: 'transparent !important',
                      },
                    }}
                  ></MIconButton>
                </Menu.Trigger>
                <Portal>
                  <Portal>
                    <Menu.Positioner>
                      <Menu.Content>
                        <MTooltip
                          label={disableUnitTypeMessage}
                          placement="bottom-end"
                          isDisabled={
                            !disableUnitTypeSelect || !disableUnitTypeMessage
                          }
                        >
                          <Menu.Item
                            key={SET_TARGET_VALUE_DISCOUNT_TYPE_OPTION.value}
                            value={SET_TARGET_VALUE_DISCOUNT_TYPE_OPTION.value}
                            onSelect={() =>
                              onInternalChangeAmountUnitType(
                                SET_TARGET_VALUE_DISCOUNT_TYPE_OPTION.value,
                              )
                            }
                            disabled={disableUnitTypeSelect}
                          >
                            {SET_TARGET_VALUE_DISCOUNT_TYPE_OPTION.title}
                          </Menu.Item>
                        </MTooltip>
                        {additionalMenuItems?.map((item, index) => (
                          <Menu.Item
                            key={`additional-${index}`}
                            value={`item-${index}`}
                          >
                            {item}
                          </Menu.Item>
                        ))}
                      </Menu.Content>
                    </Menu.Positioner>
                  </Portal>
                </Portal>
              </Menu.Root>
            )}
          </InputElement>

          {!!targetAmountOptions && isTargetAmountModalOpen && (
            <DiscountTargetPriceModal
              isOpen
              onClose={onCloseTargetAmountModal}
              targetAmountOptions={targetAmountOptions}
              onChangeValue={onChangeDiscountTargetPrice}
            />
          )}
        </Box>
      );
    },
  );

export default MAmountWithUnitTypeInput;
